import React, { useEffect, useState } from 'react';
import useGlobalSettings, {
    GlobalSettings,
    UpdateGlobalSettingsRequest,
} from '../hooks/useGlobalSettings';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function GlobalSettingsForm() {
    const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(
        null,
    );
    const [formData, setFormData] = useState<UpdateGlobalSettingsRequest>({
        systemGalleryBaseUrl: '',
        systemGalleryAlphaUrl: '',
    });
    const { getGlobalSettings, updateGlobalSettings, isLoading, error } =
        useGlobalSettings();

    useEffect(() => {
        const fetchGlobalSettings = async () => {
            const data = await getGlobalSettings();
            if (data) {
                setGlobalSettings(data);
                setFormData({
                    systemGalleryBaseUrl: data.system_gallery_base_url,
                    systemGalleryAlphaUrl: data.system_gallery_alpha_url,
                });
            }
        };

        fetchGlobalSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const result = await updateGlobalSettings(formData);
        if (result) {
            setGlobalSettings(result);
            toast.success('Global settings updated successfully!');
        }
    };

    if (error) {
        return (
            <div className="rounded-md bg-red-50 p-4 mt-4">
                <div className="text-sm text-red-700">{error}</div>
            </div>
        );
    }

    if (isLoading && !globalSettings) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <Card className="border-none">
            <CardHeader>
                <CardTitle className="text-2xl">Global Settings</CardTitle>
                <CardDescription>
                    Manage system-wide gallery URLs and other global settings
                </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="systemGalleryBaseUrl">
                            Production Gallery Base URL
                        </Label>
                        <Input
                            id="systemGalleryBaseUrl"
                            name="systemGalleryBaseUrl"
                            value={formData.systemGalleryBaseUrl}
                            onChange={handleInputChange}
                            placeholder="https://gallery.photoengineers.com"
                            required
                        />
                        <p className="text-sm text-muted-foreground">
                            The base URL for production galleries
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="systemGalleryAlphaUrl">
                            Alpha Gallery Base URL
                        </Label>
                        <Input
                            id="systemGalleryAlphaUrl"
                            name="systemGalleryAlphaUrl"
                            value={formData.systemGalleryAlphaUrl}
                            onChange={handleInputChange}
                            placeholder="https://alpha.gallery.photoengineers.com"
                            required
                        />
                        <p className="text-sm text-muted-foreground">
                            The base URL for alpha/development galleries
                        </p>
                    </div>

                    {globalSettings && (
                        <div className="text-sm text-muted-foreground mt-4">
                            Last updated:{' '}
                            {new Date(
                                globalSettings.updated_at,
                            ).toLocaleString()}
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-end">
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}
